import { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../CookieConsentProvider';

export function CookiePreferencesModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('cookies');
  const { record, savePreferences } = useCookieConsent();
  const titleId = useId();

  const [analyticsEnabled, setAnalyticsEnabled] = useState(record?.categories.analytics ?? false);

  // Sync draft when record changes (e.g. cross-tab update)
  useEffect(() => {
    setAnalyticsEnabled(record?.categories.analytics ?? false);
  }, [record]);

  const handleSave = () => {
    savePreferences({ essential: true, analytics: analyticsEnabled });
    onClose();
  };

  // Trap focus and handle Escape key
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    el.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {t('modal.title')}
          </h2>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Category rows */}
        <div className="divide-y divide-gray-100">
          {/* Essential — always on, no toggle */}
          <div className="px-6 py-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('categories.essential.name')}</p>
              <p className="text-sm text-gray-500 mt-1">{t('categories.essential.description')}</p>
            </div>
            <span className="shrink-0 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
              {t('modal.alwaysOn')}
            </span>
          </div>

          {/* Analytics — toggleable */}
          <div className="px-6 py-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('categories.analytics.name')}</p>
              <p className="text-sm text-gray-500 mt-1">{t('categories.analytics.description')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={analyticsEnabled}
              aria-label={t('categories.analytics.name')}
              onClick={() => setAnalyticsEnabled((v) => !v)}
              className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                analyticsEnabled ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  analyticsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 px-6 py-4 border-t border-gray-200">
          <Link
            to="/cookie-policy"
            onClick={onClose}
            className="text-xs text-[color:var(--color-primary)] underline self-start"
          >
            {t('modal.policyLink')}
          </Link>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              {t('modal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
            >
              {t('modal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
